import type { PiralPlugin } from 'piral-core'
import i18n from './i18n'

/**
 * Loads one locale's catalog for a pilet. It is a thin wrapper around a dynamic
 * `import()` of a JSON module, kept pilet-local because the import path is resolved
 * relative to the pilet's own bundle — which is exactly what lets webpack code-split
 * each locale into its own async chunk and fetch only the active one.
 *
 *   app.registerTranslations('watch', lng => import(`./locales/${lng}/watch.json`))
 */
export type CatalogLoader = (
  language: string
) => Promise<{ default: Record<string, unknown> } | Record<string, unknown>>

export interface PiletI18nApi {
  /**
   * Register a pilet's translation namespace on the shared i18next instance,
   * loading catalogs lazily: only the active locale (and the fallback) is fetched
   * now, and switching language pulls the new locale's chunk on demand. Call once
   * per namespace in the pilet's setup().
   */
  registerTranslations (namespace: string, load: CatalogLoader): void
}

// A JSON dynamic import resolves to a module namespace whose `default` is the
// catalog (esModuleInterop); tolerate a bare object too, for safety.
const toResources = (mod: { default?: Record<string, unknown> } | Record<string, unknown>) =>
  (mod && 'default' in mod ? mod.default : mod) as Record<string, unknown>

/**
 * Piral plugin that puts `registerTranslations` on every pilet's API. The shell
 * owns the lazy-loading and the single `languageChanged` subscription exactly once;
 * a pilet just names its namespace and hands over a local `import()` loader, staying
 * decoupled from how the shell wires i18next.
 */
export function createI18nApi (): PiralPlugin<PiletI18nApi> {
  return () => ({
    registerTranslations (namespace, load) {
      const ensure = (language: string) => {
        // Already fetched (or re-applied by a hot reload) — nothing to load.
        if (!language || i18n.hasResourceBundle(language, namespace)) return
        Promise.resolve(load(language))
          .then(mod => {
            const resources = toResources(mod)
            i18n.addResourceBundle(language, namespace, resources, true, true)
            // addResourceBundle fires i18next's 'added' store event synchronously,
            // and react-i18next re-renders every mounted useTranslation on it (the
            // shell binds `added` via `bindI18nStore`, see i18n.ts). But a CACHED
            // locale chunk makes the import() above resolve in a microtask — before
            // the components that consume this namespace (the nav menu labels, the
            // profile header action) have run the post-paint effect where
            // react-i18next subscribes to that event. Those labels miss this load and
            // stick at their raw key — the nav shows "menu" instead of "Browse". Re-add
            // on the next macrotask, once React has flushed those effects and the
            // subscribers are attached: re-adding re-fires 'added' (verified: it emits
            // even when the bundle is unchanged) so the missed components catch up. A
            // harmless no-op when nothing missed the first emit.
            setTimeout(() => i18n.addResourceBundle(language, namespace, resources, true, true), 0)
          })
          .catch(() => {
            // No catalog for this locale, or the chunk failed to load: i18next
            // falls back to the fallback language, so a missing locale degrades
            // gracefully rather than breaking the pilet.
          })
      }

      // Fetch the active locale now, plus the fallback so fallback keys resolve
      // before the viewer ever switches away from it.
      ensure(i18n.language)
      const fallback = i18n.options.fallbackLng
      const fallbackLng = Array.isArray(fallback) ? fallback[0] : fallback
      if (typeof fallbackLng === 'string') ensure(fallbackLng)

      // Pull the catalog for any locale the viewer switches to, once each.
      i18n.on('languageChanged', ensure)
    }
  })
}
